import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {SectionBase} from '../../../components/section/section-base';
import {InsuranceCoveragesSectionConfig} from './insurance-coverages-section.config';
import {ControlChange} from '../../../../core/models/control-change.model';
import {InsuranceCoverageService} from '../../../../core/services/impl/section-services/insurance-coverage/insurance-coverage.service';
import {AssetsService} from '../../../services/assets.service';
import {isKIOSKPOLIS, isMPay, isNestBank, isSkycash} from '../../../../core/utils/utils';
import {UrlParamsService} from '../../../../core/services/impl/url-params.service';
import {DeviceService} from '../../../services/device.service';
import {InsuranceCoverageModel} from '../../../../core/models/insurance-coverage.model';
import {OfferPrimaryCoverageCode} from '../../../../core/consts/offer-primary-coverage-code.enum';
import {DetailCoverageFilterClickEvent, FiltersService} from '../../../services/filters.service';
import {OfferService} from '../../../../core/services/impl/offer.service';
import {Subscription} from 'rxjs/Rx';

@Component({
  selector: 'app-insurance-coverages-section',
  templateUrl: './insurance-coverages-section.component.html',
  styleUrls: ['./insurance-coverages-section.component.scss']
})
export class InsuranceCoveragesSectionComponent extends SectionBase implements OnInit, OnDestroy {

  @Input() offersControlPanelContext: boolean = false;
  @Output() update: EventEmitter<InsuranceCoverageModel> = new EventEmitter<InsuranceCoverageModel>();
  @Output() coverageCheckedUpdate: EventEmitter<PrimaryCoverageFilterCheckEvent> = new EventEmitter<PrimaryCoverageFilterCheckEvent>();
  availableCoverages = [OfferPrimaryCoverageCode.OC, OfferPrimaryCoverageCode.NNW, OfferPrimaryCoverageCode.ASSISTANCE, OfferPrimaryCoverageCode.AC];
  private config: InsuranceCoveragesSectionConfig;
  private state: InsuranceCoverageModel; // state for offers control panel
  private acDisabled: boolean = false;
  private subscriptions: Subscription[] = [];

  get sectionTitle(): string {
    return ((isMPay() || isSkycash()) && this.urlParamsService.isMobileOrigin) ? 'INSURANCE_COVERAGES_SECTION_COMPONENT.alternateTitle' : this.title;
  }

  get titleVisible(): boolean {
    return !this.offersControlPanelContext;
  }

  get isSafari(): boolean {
    return this.deviceService.isSafari;
  }

  get nextInsuranceCoveragesSection(): boolean {
    return isKIOSKPOLIS() || isNestBank();
  }

  get allNonCoveragesChecked(): boolean {
    return this.availableCoverages
        .filter(coverage => coverage !== OfferPrimaryCoverageCode.OC)
        .map(code => this.state[code])
        .every(checked => checked);
  }

  constructor(private stateService: InsuranceCoverageService,
              private urlParamsService: UrlParamsService,
              private filtersService: FiltersService,
              private deviceService: DeviceService,
              private offerService: OfferService,
              private assetsService: AssetsService) {
    super();
    this.config = new InsuranceCoveragesSectionConfig(this.assetsService);
  }

  ngOnInit() {
    if (this.offersControlPanelContext) {
      this.state = new InsuranceCoverageModel(false);
      this.handleFiltersChange();
      this.handleOffersChange();
    }
    this.initBean();
    this.createConfig();
    this.buildForm();
    if (!this.offersControlPanelContext) {
      this.initControlsChanges();
      this.getControl(OfferPrimaryCoverageCode.OC).disable();
    }
  }

  ngOnDestroy(): void {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions = [];
  }

    createConfig(): void {
    this.formConfig = this.config.createInsuranceCoveragesConfig(this.offersControlPanelContext);
  }

  // FIXME update bean initializing after model implementation
  initBean(): void {
    this.bean = this.offersControlPanelContext ? this.state : this.stateService.insuranceCoverage;
  }

  // FIXME handling controls changes after model implementation
  initControlsChanges(): void {
    let controlsChanges: ControlChange[] = this.availableCoverages.map(coverage => ({
      controlName: coverage,
      behaviour: value => this.changeInsuranceCoverage(coverage, value)
    }));
    this.handleControlsState(...controlsChanges);
  }

  coverageClassName(coverage: string): string {
    return `insurance-coverages-section__coverage--${coverage}`;
  }

  changeInsuranceCoverage(coverage: OfferPrimaryCoverageCode, value: boolean): void {
    if (this.offersControlPanelContext) {
      setTimeout(() => {
        this.handleOffersControlPanelCoverageChange(coverage, value);
      }, 0);
    } else {
      this.stateService.changeInsuranceCoverage(coverage, value);
    }
  }

  onCoverageClick(coverage: OfferPrimaryCoverageCode): void {
    if (this.nextInsuranceCoveragesSection) {
      const checked = this.getControl(coverage).value;
      setTimeout(() => {
        this.changeInsuranceCoverage(coverage, checked);
      }, 0);
    }
  }

  isCoverageDisabled(code: string): boolean {
      return code === OfferPrimaryCoverageCode.AC && this.acDisabled;
  }

  private handleOffersControlPanelCoverageChange(coverage: OfferPrimaryCoverageCode, value: boolean, emitCoverageCheckedUpdate: boolean = true): void {
    this.state[coverage] = value;
    if (coverage !== OfferPrimaryCoverageCode.OC && value) {
      this.checkOc();
    }
    if (coverage === OfferPrimaryCoverageCode.OC && !value) {
      this.uncheckAllCoverages();
    }
    this.update.emit(this.state);
    if (emitCoverageCheckedUpdate) {
      this.coverageCheckedUpdate.emit({
        coverageCode: coverage,
        checked: value
      });
    }
  }

  private checkOc(): void {
    this.getControl(OfferPrimaryCoverageCode.OC).setValue(true);
    this.state[OfferPrimaryCoverageCode.OC] = true;
  }

  private uncheckAllCoverages(): void {
    this.availableCoverages.forEach(coverage => {
      this.getControl(coverage).setValue(false);
      this.coverageCheckedUpdate.emit({
        coverageCode: coverage,
        checked: false
      });
    });
    this.state = new InsuranceCoverageModel(false);
  }

  private handleFiltersChange(): void {
      this.subscriptions.push(
        this.filtersService.detailCoverageFilterClick.subscribe((event: DetailCoverageFilterClickEvent) => {
          const primaryCoverages = this.filtersService.getPrimaryCoveragesWithRelatedFilter(event.filterKey);
          primaryCoverages.forEach(primaryCoverageCode => {
            if (event.checked) {
              const anyRelatedFilters = this.filtersService.anyRelatedDetailCoverageFiltersActive(primaryCoverageCode);
              if (anyRelatedFilters && !this.getControl(primaryCoverageCode).value) {
                this.getControl(primaryCoverageCode).setValue(true, {emitEvent: false});
                this.handleOffersControlPanelCoverageChange(primaryCoverageCode, true, false);
              }
            }
          })
        })
    );
  }

  private handleOffersChange(): void {
      this.subscriptions.push(
          this.offerService.$offersChange.subscribe(() => {
              this.acDisabled = !this.filtersService.checkAtLeastOneAcOffer(this.offerService.offers);
          })
      );
  }

}

export interface PrimaryCoverageFilterCheckEvent {
  coverageCode: OfferPrimaryCoverageCode;
  checked: boolean;
}
